import { readCachedUser } from "../utils/cachedUser";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import NotificationBell from "../components/NotificationBell";
import { perfMark } from "../utils/perf";
import Icon from "../components/Icon";
import useIncomeTransactions, { byNewest } from "../hooks/useIncomeTransactions";

const withdrawRequestFn = httpsCallable(functions, "withdrawRequest");

const css = `
*{box-sizing:border-box;margin:0;padding:0;}
  .wp{min-height:100vh;background:var(--bg);color:var(--text);font-family:'Prompt',sans-serif;}
  .wp-nav{display:flex;align-items:center;justify-content:space-between;padding:18px 40px;background:var(--surface);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:10;}
  .wp-logo{display:flex;align-items:center;gap:8px;font-family:'DM Serif Display',serif;font-size:1.4rem;color:var(--accent);}
  .wp-logo span{color:var(--text);}
  .wp-logo-img{height:80px;width:auto;mix-blend-mode:screen;filter:brightness(1.1);}
  .wp-back{padding:7px 16px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.8rem;cursor:pointer;}
  .wp-body{max-width:680px;margin:0 auto;padding:36px 24px;}
  .wp-card{background:linear-gradient(135deg,#ffffff,#f4f5f7);border:1px solid rgba(247,71,53,0.2);border-radius:20px;padding:32px;margin-bottom:24px;}
  .wp-card-label{font-size:0.72rem;letter-spacing:2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:8px;}
  .wp-balance{font-family:'DM Serif Display',serif;font-size:3rem;color:var(--accent);margin-bottom:4px;}
  .wp-balance-sub{font-size:0.8rem;color:var(--text-muted);}
  .wp-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:24px;}
  .wp-action-btn{padding:14px;border-radius:var(--radius);border:1px solid var(--border);background:var(--surface);color:var(--text);font-family:'Prompt',sans-serif;font-size:0.85rem;font-weight:600;cursor:pointer;text-align:center;}
  .wp-action-btn.primary{background:rgba(247,71,53,0.08);border-color:rgba(247,71,53,0.3);color:var(--accent);}
  /* Debug */
  .debug-box{background:#0a1a0a;border:1px solid rgba(247,71,53,0.2);border-radius:12px;padding:16px;margin-bottom:20px;font-family:'DM Mono',monospace;font-size:0.72rem;}
  .debug-title{color:var(--accent);font-weight:700;margin-bottom:10px;}
  .debug-row{display:flex;gap:8px;margin-bottom:5px;align-items:flex-start;}
  .debug-key{color:var(--text-muted);flex-shrink:0;min-width:130px;}
  .debug-val{color:var(--text);word-break:break-all;}
  .debug-val.ok{color:var(--accent);}
  .debug-val.err{color:var(--danger);}
  .debug-val.warn{color:var(--warning);}
  .debug-sep{border:none;border-top:1px solid var(--border);margin:10px 0;}
  .debug-log{font-size:0.7rem;}
  .log-row{display:flex;gap:8px;margin-bottom:3px;}
  .log-time{color:var(--text-muted);flex-shrink:0;}
  .log-msg{color:var(--text);}
  .log-msg.err{color:var(--danger);}
  .log-msg.ok{color:var(--accent);}
  .log-msg.warn{color:var(--warning);}
  .log-msg.info{color:var(--info);}
  /* Modal */
  .wp-modal{position:fixed;inset:0;background:rgba(0,0,0,0.75);backdrop-filter:blur(8px);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
  .wp-modal-box{background:var(--surface);border:1px solid var(--border);border-radius:20px;padding:28px;width:100%;max-width:420px;max-height:90vh;overflow-y:auto;}
  .wp-modal-title{font-size:1.05rem;font-weight:700;margin-bottom:6px;}
  .wp-modal-sub{font-size:0.8rem;color:var(--text-muted);margin-bottom:18px;}
  .wp-amounts{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;}
  .wp-amount-btn{padding:12px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.88rem;font-weight:600;cursor:pointer;text-align:center;}
  .wp-amount-btn.selected{background:rgba(247,71,53,0.1);border-color:var(--accent);color:var(--accent);}
  .wp-input{width:100%;padding:11px 14px;background:#f5f6f8;border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.88rem;outline:none;margin-bottom:10px;}
  .wp-fee-preview{background:var(--surface2);border-radius:10px;padding:12px;margin-bottom:12px;font-size:0.8rem;}
  .wp-fp-row{display:flex;justify-content:space-between;margin-bottom:4px;}
  .wp-fp-row:last-child{margin-bottom:0;border-top:1px solid var(--border);padding-top:4px;font-weight:700;}
  .wp-fp-row span:last-child{color:var(--accent);}
  .wp-modal-btns{display:flex;gap:10px;}
  .wp-mb-cancel{flex:1;padding:11px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.85rem;cursor:pointer;}
  .wp-mb-ok{flex:1;padding:11px;border-radius:10px;border:none;background:var(--accent);color:#f5f6f8;font-family:'Prompt',sans-serif;font-size:0.85rem;font-weight:700;cursor:pointer;}
  .wp-mb-ok:disabled{opacity:0.5;cursor:not-allowed;}
  .wp-spinner-sm{width:14px;height:14px;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;animation:spin 0.8s linear infinite;display:inline-block;vertical-align:middle;margin-right:6px;}
  @keyframes spin{to{transform:rotate(360deg)}}
  .wp-qr-img{width:220px;height:220px;border-radius:12px;border:3px solid var(--accent);margin:12px auto;display:block;background:white;padding:8px;}
  .wp-status{display:flex;align-items:center;justify-content:center;gap:8px;padding:10px;border-radius:10px;font-size:0.82rem;margin-bottom:12px;}
  .wp-status.waiting{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.2);color:var(--warning);}
  .wp-status.success{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.2);color:var(--accent);}
  .wp-err{background:rgba(255,85,85,0.1);border:1px solid rgba(255,85,85,0.3);border-radius:10px;padding:10px 14px;color:var(--danger);font-size:0.8rem;margin-bottom:12px;}
  .wp-notice{background:rgba(85,153,255,0.08);border:1px solid rgba(85,153,255,0.2);border-radius:10px;padding:12px 14px;font-size:0.78rem;color:var(--info);margin-bottom:12px;line-height:1.6;}
  .wp-sec-label{font-size:0.7rem;letter-spacing:2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:14px;}
  .wp-txs{display:flex;flex-direction:column;gap:8px;}
  .wp-tx{display:flex;align-items:center;gap:12px;padding:14px 16px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);}
  .wp-tx-icon{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.1rem;flex-shrink:0;background:rgba(247,71,53,0.1);}
  .wp-tx-info{flex:1;}
  .wp-tx-name{font-size:0.85rem;font-weight:600;}
  .wp-tx-sub{font-size:0.72rem;color:var(--text-muted);margin-top:2px;}
  .wp-tx-amount{font-size:0.9rem;font-weight:700;color:var(--accent);}
  .wp-empty{text-align:center;padding:32px;color:var(--text-muted);font-size:0.85rem;}
  .wp-select{width:100%;padding:11px 14px;background:#f5f6f8;border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.88rem;outline:none;margin-bottom:10px;appearance:none;cursor:pointer;}
  .wp-select option{background:#ffffff;}
  .wp-wd-pending{background:rgba(255,179,71,0.06);border:1px solid rgba(255,179,71,0.2);border-radius:10px;padding:10px 14px;font-size:0.78rem;color:var(--warning);margin-bottom:14px;}
  .wp-locked-bar{display:flex;align-items:center;justify-content:space-between;background:rgba(255,179,71,0.07);border:1px solid rgba(255,179,71,0.18);border-radius:10px;padding:9px 14px;margin-top:12px;font-size:0.8rem;color:var(--warning);}
  .wp-locked-bar strong{font-weight:700;}
  .wp-locked-bar .wp-locked-sub{font-size:0.7rem;opacity:0.7;margin-top:1px;}
  .wp-tx-amount.neg{color:var(--danger)!important;}
  /* Bank card */
  .wp-bank-card{background:rgba(247,71,53,0.06);border:1px solid rgba(247,71,53,0.2);border-radius:12px;padding:14px 16px;margin-bottom:14px;text-align:center;}
  .wp-bank-name{font-size:0.78rem;color:var(--text-muted);margin-bottom:4px;}
  .wp-bank-acc{font-size:1.4rem;font-weight:700;color:var(--accent);letter-spacing:2px;margin-bottom:4px;}
  .wp-wd-account{background:rgba(247,71,53,0.05);border:1px solid rgba(247,71,53,0.2);border-radius:12px;padding:14px 16px;margin-bottom:14px;}
  .wp-wd-account-head{display:flex;justify-content:space-between;align-items:center;gap:10px;font-size:0.72rem;letter-spacing:0.5px;color:var(--text-muted);margin-bottom:8px;}
  .wp-wd-account-edit{background:none;border:none;color:var(--accent);font-family:'Prompt',sans-serif;font-size:0.78rem;font-weight:600;cursor:pointer;padding:4px 0;text-decoration:underline;}
  .wp-wd-account-no{font-size:1.15rem;font-weight:700;color:var(--text);letter-spacing:2px;margin:2px 0;}
  .wp-wd-missing{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.35);border-radius:12px;padding:14px 16px;margin-bottom:14px;font-size:0.84rem;line-height:1.6;color:#8a5a14;}
  .wp-wd-missing button{display:block;margin-top:10px;padding:9px 16px;border-radius:10px;border:none;background:var(--accent);color:#fff;font-family:'Prompt',sans-serif;font-weight:600;cursor:pointer;}
  .wp-bank-owner{font-size:0.85rem;color:var(--text);font-weight:600;}
  .wp-copy-acc{margin-top:8px;padding:5px 14px;border-radius:100px;border:1px solid rgba(247,71,53,0.3);background:transparent;color:var(--accent);font-family:'Prompt',sans-serif;font-size:0.72rem;cursor:pointer;}
  /* Slip upload */
  .wp-slip-zone{border:2px dashed var(--border);border-radius:12px;padding:18px;text-align:center;cursor:pointer;margin-bottom:12px;position:relative;transition:border-color 0.2s;}
  .wp-slip-zone:hover,.wp-slip-zone.has-file{border-color:rgba(247,71,53,0.4);}
  .wp-slip-zone input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%;}
  .wp-slip-label{font-size:0.8rem;color:var(--text-muted);}
  .wp-slip-preview{width:100%;max-height:200px;object-fit:contain;border-radius:8px;margin-top:8px;border:1px solid var(--border);}
  /* Pending topup bar */
  .wp-topup-pending{background:rgba(85,153,255,0.07);border:1px solid rgba(85,153,255,0.2);border-radius:10px;padding:10px 14px;font-size:0.78rem;color:var(--info);margin-bottom:14px;}
  @media(max-width:600px){
    .wp-nav{padding:12px 14px;}
    .wp-logo{font-size:1.1rem;}
    .wp-logo-img{height:20px;}
    .wp-body{padding:20px 14px;}
    .wp-actions{grid-template-columns:1fr;}
    .wp-card{padding:24px 20px;}
    .wp-balance{font-size:2.2rem;}
    .wp-modal-box{border-radius:20px 20px 0 0;position:fixed;bottom:0;left:0;right:0;max-width:100%;max-height:92vh;padding:24px 20px;}
    .wp-modal{align-items:flex-end;padding:0;}
    .wp-amounts{grid-template-columns:1fr 1fr;}
    .wp-qr-img{width:180px;height:180px;}
    .wp-tx{padding:12px 14px;gap:10px;}
    .wp-tx-icon{width:34px;height:34px;font-size:1rem;}
    .wp-tx-name{font-size:0.82rem;}
    .wp-modal-btns{flex-direction:column;}
  }
  @media(max-width:360px){
    .wp-balance{font-size:2rem;}
    .wp-card{padding:20px 16px;}
    .wp-tx-amount{font-size:0.82rem;}
  }
`;

const TOP_UP_AMOUNTS = [100, 500, 1000, 5000, 10000, 50000];
const TX_META = {
  topup:            { icon: "coin", label: "เติมเงิน" },
  lock:             { icon: "lock", label: "ชำระเงิน (Escrow)" },
  release:          { icon: "check", label: "รับเงิน (ขายสำเร็จ)" },
  coupon_share:     { icon: "coin", label: "ส่วนแบ่งจากคูปอง" },
  withdraw_reject:  { icon: "undo", label: "ถอนเงินไม่สำเร็จ (คืนเงินเข้ากระเป๋า)" },
  refund:           { icon: "undo", label: "คืนเงิน" },
  withdraw_request: { icon: "clock", label: "รอถอนเงิน (รอ Admin)" },
  withdraw:         { icon: "bank", label: "ถอนเงินสำเร็จ" },
};


// บัญชีรับเงินที่บันทึกไว้ในโปรไฟล์ (users/{uid}.bankAccount) — กติกาเดียวกับ server (withdrawRequest) แต่ใช้แสดงผลเท่านั้น
function readProfileBank(raw) {
  if (!raw || typeof raw !== "object") return null;
  const bankName = String(raw.bankName || "").trim();
  const accountNumber = String(raw.accountNumber || "").replace(/-/g, "").trim();
  const accountName = String(raw.accountName || "").trim();
  if (!bankName || !accountName || !/^\d{10,15}$/.test(accountNumber)) return null;
  return { bankName, accountNumber, accountName };
}
const maskAccount = (n) => `${"•".repeat(Math.max(0, n.length - 4))}${n.slice(-4)}`;

export default function WalletPage() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({
    wallet_balance: 0,
    locked_balance: 0,
  });
  const [ownTxs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [wdErr, setWdErr] = useState("");
  const [dataError, setDataError] = useState("");
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [slipLoading, setSlipLoading] = useState(false);
  const [slipSuccess, setSlipSuccess] = useState(false);
  const [pendingTopup, setPendingTopup] = useState(null);
  const [accCopied, setAccCopied] = useState(false);
  // withdraw state
  const [wdAmount, setWdAmount] = useState("");
  const [wdLoading, setWdLoading] = useState(false);
  const [wdSuccess, setWdSuccess] = useState(false);

  const [pendingWd, setPendingWd] = useState(null); // คำขอถอนที่ยังรอ Admin

  const user = readCachedUser();
  // ถอนเงินได้เฉพาะเข้าบัญชีที่บันทึกในหน้าโปรไฟล์เท่านั้น (ไม่ให้พิมพ์บัญชีใหม่ที่หน้านี้)
  const profileBank = readProfileBank(wallet.bankAccount);
  const rawAmount = Number(topupAmount) || selectedPreset || 0;
  useEffect(() => () => { if (slipPreview) URL.revokeObjectURL(slipPreview); }, [slipPreview]);

  useEffect(() => {
    if (!user.uid) { navigate("/login"); return; }
    const doneFirst = perfMark("WalletPage: onSnapshot(users/me) ครั้งแรก");
    let first = true;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (first) { doneFirst("exists=" + snap.exists()); first = false; }
      if (snap.exists()) setWallet(snap.data());
      setLoading(false);
    }, (err) => {
      if (first) { doneFirst("error: " + err.code); first = false; }
      console.error("[WalletPage] wallet listener error:", err);
      setDataError("โหลดข้อมูลกระเป๋าเงินไม่สำเร็จ กรุณาเปิดหน้าใหม่ก่อนทำรายการ");
      setLoading(false);
      if (err.code === "permission-denied" || err.code === "unauthenticated") {
        localStorage.removeItem("klang-user");
        navigate("/login");
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.uid) return;
    const q = query(
      collection(db, "transactions"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) =>
      setTxs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setDataError("โหลดประวัติธุรกรรมไม่สำเร็จ กรุณาเปิดหน้าใหม่ก่อนทำรายการ"),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // โหลด pending withdrawal ของ user
  useEffect(() => {
    if (!user.uid) return;
    const q = query(
      collection(db, "withdrawals"),
      where("uid", "==", user.uid),
      where("status", "==", "pending"),
      limit(1),
    );
    return onSnapshot(q, (snap) => {
      setPendingWd(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    }, () => setDataError("ตรวจสอบคำขอถอนเงินไม่สำเร็จ กรุณาเปิดหน้าใหม่ก่อนทำรายการ"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // โหลด pending topup request ของ user
  useEffect(() => {
    if (!user.uid) return;
    const q = query(
      collection(db, "topup_requests"),
      where("uid", "==", user.uid),
      where("status", "==", "pending"),
      limit(1),
    );
    return onSnapshot(q, (snap) => {
      setPendingTopup(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    }, () => setDataError("ตรวจสอบคำขอเติมเงินไม่สำเร็จ กรุณาเปิดหน้าใหม่ก่อนทำรายการ"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("อ่านรูปภาพไม่สำเร็จ กรุณาใช้ไฟล์ JPG, PNG หรือ WebP")); };
    img.onload = () => {
      try {
      const MAX = 900;
      let w = img.width, h = img.height;
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.65));
      } catch (_) { reject(new Error("ประมวลผลรูปภาพไม่สำเร็จ กรุณาเลือกรูปใหม่")); }
      finally { URL.revokeObjectURL(objectUrl); }
    };
    img.src = objectUrl;
  });

  // (ประกาศหลัง effect เดิมทั้งหมด เพื่อไม่เปลี่ยนลำดับการลงทะเบียน onSnapshot ที่เทสต์อ้างอิง)
  // รวมรายการที่ผู้ใช้ "ได้รับเงิน" ในฐานะผู้ขาย/เจ้าของคูปอง (ไม่มี userId ในธุรกรรม จึงค้นด้วย userId อย่างเดียวไม่เจอ)
  const incomeTxs = useIncomeTransactions(user.uid);
  const txs = useMemo(() => [...ownTxs, ...incomeTxs].sort(byNewest), [ownTxs, incomeTxs]);

  const handleSubmitTopup = async () => {
    if (pendingTopup || slipLoading) return;
    if (dataError) { setErrMsg(dataError); return; }
    if (!Number.isSafeInteger(rawAmount) || rawAmount < 100 || rawAmount > 1000000) { setErrMsg("กรุณาระบุจำนวนเต็มตั้งแต่ ฿100 ถึง ฿1,000,000"); return; }
    if (!slipFile) { setErrMsg("กรุณาแนบสลิปการโอน"); return; }
    if (slipFile.size > 10 * 1024 * 1024) { setErrMsg("รูปสลิปต้องมีขนาดไม่เกิน 10 MB"); return; }
    setSlipLoading(true); setErrMsg("");
    try {
      const slipBase64 = await compressImage(slipFile);
      await addDoc(collection(db, "topup_requests"), {
        uid: user.uid,
        amount: rawAmount,
        slipBase64,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setSlipSuccess(true);
      setSelectedPreset(null); setTopupAmount(""); setSlipFile(null); setSlipPreview(null);
    } catch (err) {
      setErrMsg(err?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    }
    setSlipLoading(false);
  };

  return (
    <div className="wp">
      <style>{css}</style>
      <nav className="wp-nav">
        <div className="wp-logo" onClick={() => navigate("/home")} style={{cursor:"pointer"}}>
          <span className="kl-wordmark">klang.</span> · Wallet
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <NotificationBell />
          <button className="wp-back" onClick={() => navigate("/home")}>
            ← กลับ
          </button>
        </div>
      </nav>

      <h1 className="kl-page-title">กระเป๋าเงิน</h1>
      <p className="kl-page-subtitle">ฝาก ถอน และติดตามรายการเงินของคุณ</p>
      {dataError && <p role="alert" className="wp-err">{dataError} <button type="button" onClick={() => window.location.reload()}>เปิดหน้าใหม่</button></p>}
      <div className="wp-body">
        <div className="wp-summary">
        <div className="wp-card wp-available">
          <div className="wp-card-label">ยอดเงินในกระเป๋า</div>
          <div className="wp-balance">
            {loading
              ? "..."
              : `฿${(wallet.wallet_balance || 0).toLocaleString()}`}
          </div>
          <div className="wp-balance-sub">พร้อมใช้งาน</div>
        </div>
        <div className="wp-card"><div className="wp-card-label"><Icon name="lock" size={20} /> เงินพักในดีล</div><strong className="wp-summary-amount">{loading ? "..." : `฿${Math.max(0, (wallet.locked_balance || 0) - (pendingWd?.amount || 0)).toLocaleString()}`}</strong></div>
        <div className="wp-card"><div className="wp-card-label"><Icon name="clock" size={20} /> ถอนเงินรอดำเนินการ</div><strong className="wp-summary-amount">฿{(pendingWd?.amount || 0).toLocaleString()}</strong></div>
        </div>

        {pendingTopup && (
          <div className="wp-topup-pending">
            มีคำขอเติมเงิน <strong>฿{(pendingTopup.amount||0).toLocaleString()}</strong> รอ Admin ตรวจสลิปอยู่
          </div>
        )}

      </div>
      <div className="wp-transfer-grid">
      {/* Modal แจ้งโอนเงิน */}
      {(
        <section className="wp-transfer-panel wp-deposit" aria-label="ฝากเงิน">
          <div className="wp-modal-box">
            {slipSuccess ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ marginBottom:12, color:"#dc3728" }}><Icon name="check" size={48} /></div>
                <div className="wp-status success">ส่งสลิปแล้ว!</div>
                <div style={{ color:"var(--text-muted)", fontSize:"0.82rem", marginBottom:16 }}>
                  Admin จะตรวจสอบและเติมเงินให้ภายใน 10–30 นาที
                </div>
                <button className="wp-mb-ok" style={{ width:"100%" }} onClick={() => { setSlipSuccess(false); setSlipFile(null); setSlipPreview(null); }}>ทำรายการใหม่</button>
              </div>
            ) : (
              <>
                <div className="wp-modal-title"><Icon name="wallet" size={26} /> ฝากเงิน</div>
                <div className="wp-modal-sub">โอนมาที่บัญชีด้านล่าง แล้วแนบสลิปยืนยัน</div>

                {/* ข้อมูลบัญชี */}
                <div className="wp-bank-card">
                  <div className="wp-bank-name">ธนาคารกสิกรไทย (KBANK)</div>
                  <div className="wp-bank-acc">220-3-21995-7</div>
                  <div className="wp-bank-owner">บจก. แมคสโตร์ เซอร์วิสแอนด์รีแพร์</div>
                  <button className="wp-copy-acc" onClick={() => {
                    navigator.clipboard.writeText("2203219957");
                    setAccCopied(true);
                    setTimeout(() => setAccCopied(false), 2000);
                  }}>
                    {accCopied ? "✓ คัดลอกแล้ว" : "คัดลอกเลขบัญชี"}
                  </button>
                </div>

                {errMsg && <div className="wp-err">{errMsg}</div>}

                {/* เลือกจำนวน */}
                <div className="wp-amounts">
                  {TOP_UP_AMOUNTS.map((amt) => (
                    <button key={amt}
                      className={`wp-amount-btn${selectedPreset === amt ? " selected" : ""}`}
                      onClick={() => { setSelectedPreset(amt); setTopupAmount(""); }}>
                      ฿{amt.toLocaleString()}
                    </button>
                  ))}
                </div>
                <label className="wp-field-label" htmlFor="deposit-amount">จำนวนเงินที่ต้องการฝาก (บาท)</label>
                <input id="deposit-amount" className="wp-input" type="number" min="100" max="1000000" placeholder="หรือกรอกจำนวนที่โอน (฿)"
                  value={topupAmount}
                  onChange={(e) => { setTopupAmount(e.target.value); setSelectedPreset(null); }} />

                {rawAmount > 0 && (
                  <div className="wp-fee-preview">
                    <div className="wp-fp-row"><span>จำนวนที่โอน</span><span>฿{rawAmount.toLocaleString()}</span></div>
                    <div className="wp-fp-row"><span>ยอดที่เข้า Wallet</span><span>฿{rawAmount.toLocaleString()}</span></div>
                  </div>
                )}

                {/* แนบสลิป */}
                <div className={`wp-slip-zone${slipFile ? " has-file" : ""}`}>
                  <input aria-label="แนบหลักฐานการโอนเงิน" type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    setSlipFile(f);
                    setSlipPreview(URL.createObjectURL(f));
                  }} />
                  {slipPreview
                    ? <img src={slipPreview} alt="slip" className="wp-slip-preview" />
                    : <div className="wp-slip-label">แตะเพื่อแนบสลิปการโอน</div>}
                </div>

                <div className="wp-modal-btns">

                  <button className="wp-mb-ok" onClick={handleSubmitTopup}
                    disabled={!!dataError || loading || !!pendingTopup || !rawAmount || !slipFile || slipLoading}>
                    {slipLoading ? <><span className="wp-spinner-sm"/>กำลังส่ง...</> : "ส่งคำขอฝากเงิน"}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}
      {/* Modal ถอนเงิน */}
      {(
        <section className="wp-transfer-panel wp-withdraw" aria-label="ถอนเงิน">
          <div className="wp-modal-box">
            <div className="wp-modal-title"><Icon name="wallet" size={26} /> ถอนเงิน</div>
            <div className="wp-modal-sub">Admin จะโอนเงินให้ภายใน 1-2 วันทำการ</div>
            {pendingWd && <p className="wp-topup-pending" role="status">มีคำขอถอนเงินรออนุมัติอยู่ กรุณารอผลก่อนส่งคำขอใหม่</p>}
            {wdErr && <div className="wp-err">{wdErr}</div>}
            {wdSuccess ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ marginBottom:12, color:"#dc3728" }}><Icon name="check" size={48} /></div>
                <div className="wp-status success">ส่งคำขอถอนเงินแล้ว!</div>
                <div style={{ color:"var(--text-muted)", fontSize:"0.82rem", marginBottom:16 }}>
                  Admin จะตรวจสอบและโอนเงินให้ภายใน 1-2 วันทำการ
                </div>
                <button className="wp-mb-ok" style={{ width:"100%" }} onClick={() => setWdSuccess(false)}>ทำรายการใหม่</button>
              </div>
            ) : (
              <>
                <div className="wp-wd-pending">
                  ยอดที่ถอนได้: <strong>฿{(wallet.wallet_balance || 0).toLocaleString()}</strong>
                </div>
                {profileBank ? (
                  <div className="wp-wd-account" role="group" aria-label="บัญชีรับเงินจากโปรไฟล์">
                    <div className="wp-wd-account-head">
                      <span>โอนเข้าบัญชีที่บันทึกในโปรไฟล์</span>
                      <button type="button" className="wp-wd-account-edit" onClick={() => navigate("/profile")}>เปลี่ยนที่โปรไฟล์</button>
                    </div>
                    <div className="wp-bank-name">{profileBank.bankName}</div>
                    <div className="wp-wd-account-no">{maskAccount(profileBank.accountNumber)}</div>
                    <div className="wp-bank-owner">{profileBank.accountName}</div>
                  </div>
                ) : (
                  <div className="wp-wd-missing" role="alert">
                    ยังไม่ได้บันทึกบัญชีธนาคารสำหรับถอนเงิน — กรุณาบันทึกบัญชีที่หน้าโปรไฟล์ก่อน จึงจะส่งคำขอถอนเงินได้
                    <button type="button" onClick={() => navigate("/profile")}>ไปบันทึกบัญชีที่โปรไฟล์</button>
                  </div>
                )}
                <label className="wp-field-label" htmlFor="withdraw-amount">จำนวนเงินที่ต้องการถอน (บาท)</label>
                <input id="withdraw-amount" className="wp-input" type="number" placeholder="จำนวนเงินที่ถอน (฿)" value={wdAmount}
                  onChange={e => setWdAmount(e.target.value)} min={100} max={wallet.wallet_balance || 0} />
                {wdAmount >= 100 && (
                  <div className="wp-fee-preview">
                    <div className="wp-fp-row"><span>ถอน</span><span>฿{Number(wdAmount).toLocaleString()}</span></div>
                    <div className="wp-fp-row"><span>รับเข้าบัญชี</span><span>฿{Number(wdAmount).toLocaleString()}</span></div>
                  </div>
                )}
                <div className="wp-modal-btns">

                  <button className="wp-mb-ok" disabled={!!dataError || loading || !!pendingWd || wdLoading || !profileBank || !wdAmount}
                    onClick={async () => {
                      if (dataError) { setWdErr(dataError); return; }
                      if (pendingWd || wdLoading || !profileBank) return;
                      const amount = Number(wdAmount);
                      if (!Number.isSafeInteger(amount) || amount < 100 || amount > wallet.wallet_balance) { setWdErr("ระบุจำนวนเต็มตั้งแต่ ฿100 และไม่เกินยอดเงินพร้อมใช้"); return; }
                      setWdLoading(true); setWdErr("");
                      try {
                        // ส่งเฉพาะจำนวนเงิน — server อ่านบัญชีรับเงินจากโปรไฟล์เอง
                        await withdrawRequestFn({ amount: Number(wdAmount) });
                        setWdSuccess(true);
                        setWdAmount("");
                      } catch(err) { setWdErr(err.message); }
                      setWdLoading(false);
                    }}>
                    {wdLoading ? <><span className="wp-spinner-sm"/>กำลังส่ง...</> : "ส่งคำขอถอนเงิน"}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      </div>
      <section className="wp-history" aria-label="รายการล่าสุด">
        <div className="wp-sec-label">รายการล่าสุด ({txs.length}) <button type="button" className="wp-back" onClick={() => navigate("/transactions")}>ดูประวัติทั้งหมด</button></div>
        {txs.length === 0 ? (
          <div className="wp-empty">ยังไม่มีรายการ</div>
        ) : (
          <div className="wp-txs">
            {txs.map((tx) => {
              const meta = TX_META[tx.type] || { icon: "coin", label: tx.type };
              return (
                <div key={tx.id} className="wp-tx">
                  <div className="wp-tx-icon"><Icon name={meta.icon} size={18} /></div>
                  <div className="wp-tx-info">
                    <div className="wp-tx-name">{meta.label}</div>
                    <div className="wp-tx-sub">
                      {tx.createdAt?.toDate?.()?.toLocaleDateString("th-TH", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      }) || ""}
                    </div>
                  </div>
                  <div className={`wp-tx-amount${["lock","withdraw_request","withdraw"].includes(tx.type) ? " neg" : ""}`}>
                    {["lock","withdraw_request","withdraw"].includes(tx.type) ? "-" : "+"}฿{(tx.amount || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
